import { useDispatch, useSelector } from "react-redux";

import { useGetSettings } from "@/api/configuration/useConfigurationAPI";
import { LookupIcon, ProfilingIcon } from "@/assets/icons";
import CoverageTypeSection from "@/components/spatialAnalysis/spatialOutputAnalysis/coverageType";
import {
  buildGeohashOptions,
  buildH3Options,
  COVERAGE_TYPE_BY_VALUE
} from "@/components/spatialAnalysis/spatialOutputAnalysis/utils";
import TabSelect from "@/components/tabSelect";
import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import { MESSAGE } from "@/constants/message";
import { useValidationAnalysisConfigInput } from "@/hooks/spatialAnalysis/useValidationAnalysisConfig";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { setSpatialConfig } from "@/store/slice/spatialSlice";
import {
  EOutputAnalysisType,
  EOutputCoverageType,
  EOutputGridType,
  OutputGridType
} from "@/types/spatial";

import { twMerge } from "tailwind-merge";

import { H3CustomOption } from "./data";
import GridOutput from "./gridOutput";
import ProfilingOutput from "./profilingOutput";

const OUTPUT_VISUALIZATION = [
  {
    value: EOutputAnalysisType.GRID,
    label: "Grid",
    icon: <LookupIcon />
  },
  {
    value: EOutputAnalysisType.PROFILING,
    label: "Profiling",
    icon: <ProfilingIcon />
  }
];

export const renderH3Option = (option: H3CustomOption) => {
  return (
    <div className="flex justify-between">
      <span
        className={cn(
          "text-xs text-txtHeading w-1/4",
          option.disabled && "text-gray-300"
        )}
      >
        {option.value}
      </span>
      <span
        className={cn(
          "text-xs text-txtHeading w-1/4",
          option.disabled && "text-gray-300"
        )}
      >
        {option.radius}
      </span>
      <span
        className={cn(
          "text-xs text-txtHeading text-right w-2/4",
          option.disabled && "text-gray-300"
        )}
      >
        {option.label}
      </span>
    </div>
  );
};

const SpatialOutputAnalysis = ({
  isOutputValidationError = false,
  isResolutionValidationError = false
}: {
  isOutputValidationError?: boolean;
  isResolutionValidationError?: boolean;
}) => {
  const dispatch = useDispatch();
  const { spatialConfig } = useSelector((state: RootState) => state.spatial);
  const { validationErrors } = useValidationAnalysisConfigInput();
  const { isCoverageTypeEnabled, isNewCatchmentEstimationGrid } =
    useAppFeatures();
  const { data: analysisSettings } = useGetSettings();

  const handleChangeGridType = (value: OutputGridType) =>
    dispatch(setSpatialConfig({ ...spatialConfig, gridType: value }));

  const handleChangeGridResolution = (value: IOptionItem | null) =>
    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        gridResolution: value
      })
    );

  const handleChangeH3Resolution = (value: H3CustomOption | null) =>
    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        h3Resolution: value
      })
    );

  const handleChangeCoverageType = (value: IOptionItem | null) => {
    const outputMode =
      (value?.value as EOutputCoverageType) ?? EOutputCoverageType.STANDARD;

    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        outputMode
      })
    );
  };

  const handleChangeCatchment = (value: number | null) =>
    dispatch(
      setSpatialConfig({ ...spatialConfig, customCatchmentRadius: value })
    );

  // Default to Standard when no coverage type is set yet.
  const selectedCoverageType =
    COVERAGE_TYPE_BY_VALUE[
      spatialConfig?.outputMode ?? EOutputCoverageType.STANDARD
    ];

  const isGridTypeSelected =
    spatialConfig?.gridType === EOutputGridType.GEOHASH ||
    spatialConfig?.gridType === EOutputGridType.H3;

  const renderGeohashOption = (option: IOptionItem) => (
    <span
      className={cn(
        "text-xs text-txtHeading",
        option.disabled && "text-gray-300"
      )}
    >
      {option.label}
    </span>
  );

  return (
    <div className="flex flex-col px-4 gap-3 mt-4">
      <div className="flex flex-col gap-2">
        <span className="font-sans font-semibold text-txtHeading text-sm">
          Output Analysis
        </span>

        <p className="text-txtBody font-sans text-[10px]">
          {MESSAGE.OUTPUT_ANALYSIS_DESCRIPTION}
        </p>
      </div>

      <div
        className={twMerge(
          isOutputValidationError &&
            "border border-cinnabar rounded-xl px-2 pt-1"
        )}
      >
        <div className="flex h-[38px] bg-whisper rounded-md items-center px-1 mt-1">
          <TabSelect
            tabList={OUTPUT_VISUALIZATION}
            selected={spatialConfig?.outputType}
            className="bg-transparent w-full mt-0 px-0"
            buttonClassName="flex"
            buttonSelectedClassName="!bg-white !border-teal-500 font-bold"
            wrapperListClassName="w-full justify-between gap-1"
            onSelect={(value) => {
              dispatch(
                setSpatialConfig({
                  ...spatialConfig,
                  outputType: value as EOutputAnalysisType
                })
              );
            }}
          />
        </div>
        <div className="flex flex-col my-2 gap-3">
          {spatialConfig?.outputType === EOutputAnalysisType.GRID && (
            <GridOutput
              selected={spatialConfig.gridType}
              onChange={handleChangeGridType}
            />
          )}
          {spatialConfig?.outputType === EOutputAnalysisType.PROFILING && (
            <ProfilingOutput />
          )}
        </div>
      </div>
      {isOutputValidationError && (
        <p className="text-[10px] text-cinnabar self-start -mt-2">
          {validationErrors.outputAnalysisMessage}
        </p>
      )}
      {spatialConfig.outputType === EOutputAnalysisType.GRID &&
        isGridTypeSelected && (
          <>
            {/* Coverage Type */}
            {isCoverageTypeEnabled && (
              <CoverageTypeSection
                selectedCoverageType={selectedCoverageType}
                valueCatchment={spatialConfig?.customCatchmentRadius ?? null}
                onHandleChangeCoverageType={handleChangeCoverageType}
                onHandleCatchment={handleChangeCatchment}
              />
            )}

            {/* Resolution settings */}
            <div className="flex flex-col gap-2">
              <span className="text-txtHeading font-semibold font-sans text-sm">
                Resolution Settings
              </span>

              {spatialConfig?.gridType === EOutputGridType.GEOHASH && (
                <Dropdown
                  isValidationError={isResolutionValidationError}
                  placeholder="Resolution"
                  value={spatialConfig.gridResolution}
                  data={buildGeohashOptions({
                    areaSize: spatialConfig.areaSize,
                    maxGridCellCount: analysisSettings?.max_geohash_grid_cells,
                    isNewEstimation: isNewCatchmentEstimationGrid
                  })}
                  renderOption={renderGeohashOption}
                  onSelect={handleChangeGridResolution}
                />
              )}

              {spatialConfig?.gridType === EOutputGridType.H3 && (
                <Dropdown
                  isValidationError={isResolutionValidationError}
                  placeholder="Resolution"
                  value={spatialConfig.h3Resolution}
                  data={buildH3Options({
                    areaSize: spatialConfig.areaSize,
                    maxGridCellCount: analysisSettings?.max_h3_grid_cells,
                    isNewEstimation: isNewCatchmentEstimationGrid
                  })}
                  onSelect={handleChangeH3Resolution}
                  renderOption={renderH3Option}
                  renderSelectValue={renderH3Option}
                />
              )}

              {isResolutionValidationError && (
                <p className="-mt-1 text-[10px] text-cinnabar self-start">
                  {validationErrors.resolutionMessage}
                </p>
              )}
            </div>
          </>
        )}
    </div>
  );
};

export default SpatialOutputAnalysis;
